import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';

import { useUsage } from './billing';
import { startInstance } from './data';
import { useInFlightGuard } from './useInFlightGuard';

/**
 * Start a run from a template — the flow the Home and template screens share:
 * enforce the free-plan run limit (route to the paywall instead of starting),
 * spin up the instance locally, and navigate to it, with a `starting` busy flag
 * and a surfaced `error`. `startRun` resolves `true` only when a run actually
 * started and navigation happened (so callers can e.g. close a sheet on success);
 * it resolves `false` on a paywall redirect or a failure (with `error` set), and
 * `undefined` if a start was already in flight (the double-tap is dropped).
 */
export function useStartRun() {
  const router = useRouter();
  const usage = useUsage();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runExclusive = useInFlightGuard();

  const startRun = useCallback(
    (templateId: string): Promise<boolean | undefined> =>
      runExclusive(async () => {
        if (usage.atRunLimit) {
          router.push('/paywall');
          return false;
        }
        setStarting(true);
        setError(null);
        try {
          const instanceId = await startInstance(templateId);
          router.push(`/instance/${instanceId}`);
          return true;
        } catch (e) {
          setError((e as Error).message);
          return false;
        } finally {
          setStarting(false);
        }
      }),
    [router, usage.atRunLimit, runExclusive],
  );

  return { startRun, starting, error };
}
